import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Trim whitespace from inputs (common mobile keyboard issue)
    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    
    if (!trimmedUsername || !trimmedPassword) {
      toast.error("Please enter username and password");
      return;
    }
    
    setLoading(true);
    console.log("[Login] Attempting login for:", trimmedUsername);
    console.log("[Login] API URL:", API);
    
    try {
      // Add timeout for slow mobile connections
      const response = await axios.post(`${API}/auth/login`, {
        username: trimmedUsername,
        password: trimmedPassword
      }, {
        timeout: 30000, // 30 second timeout for slow connections
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log("[Login] Response received:", response.status);
      
      if (response.data && response.data.access_token) {
        console.log("[Login] Login successful, token received");
        toast.success("Login successful!");
        onLogin(response.data.access_token, response.data.user);
      } else {
        console.error("[Login] Invalid response structure:", response.data);
        toast.error("Invalid response from server");
      }
    } catch (error) {
      console.error("[Login] Error:", error);
      console.error("[Login] Error code:", error.code);
      console.error("[Login] Error message:", error.message);
      console.error("[Login] Error response:", error.response);
      
      if (error.code === 'ECONNABORTED') {
        toast.error("Connection timeout. Please check your internet connection.");
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error("Network error. Please check your internet connection and try again.");
      } else if (error.response?.status === 401) {
        toast.error("Invalid username or password");
      } else if (error.response?.status === 0 || !error.response) {
        toast.error("Cannot connect to server. Please check your internet connection.");
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5] p-4" 
         style={{
           backgroundImage: `url('https://customer-assets.emergentagent.com/job_bcc851c7-6465-41a4-a49f-44dbf65b5fb7/artifacts/jyj5cu5a_unity-hp-bg.jpg')`,
           backgroundSize: 'cover',
           backgroundPosition: 'center'
         }}>
      <div className="absolute inset-0 bg-black/50"></div>
      <div className="relative w-full max-w-md">
        <div className="bg-white border-2 border-black/10 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-lg overflow-hidden mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_bcc851c7-6465-41a4-a49f-44dbf65b5fb7/artifacts/72dfskcd_unity-contractors-logo.jpg" 
                alt="Unity Contractors Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">
              UNITY DELIVERY TRACKER
            </h1>
            <p className="text-sm text-muted-foreground font-body mt-2">
              Soil Disposal Tracking System
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold font-body mb-2" data-testid="username-label">
                USERNAME
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                className="w-full h-12 px-4 bg-transparent border-2 border-black/10 focus:border-primary focus:outline-none font-mono text-sm"
                placeholder="Enter username"
                data-testid="username-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold font-body mb-2" data-testid="password-label">
                PASSWORD
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full h-12 px-4 pr-12 bg-transparent border-2 border-black/10 focus:border-primary focus:outline-none font-mono text-sm"
                  placeholder="Enter password"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none p-2"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-primary text-white font-heading font-bold text-lg uppercase tracking-wide shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px] transition-all disabled:opacity-50 touch-manipulation"
              data-testid="login-button"
            >
              {loading ? "LOGGING IN..." : "LOGIN"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}